import { Box, Card, Chip, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { IHeaderGrid } from "../../../interfaces/grid/IHeaderGrid";
import GridContent from "../../../components/grid";
import GridHeader from "../../../components/grid/header";
import { useEffect, useState } from "react";
import useLazyLoading from "../../../common/hooks/loader";
import Loader from "../../../components/loader";
import { useNotification } from "../../../components/notification/useNotification";
import { IProfitLossResumedDto } from "../../../interfaces/dashboard/IProfitLossResumedDto";
import { tryGetProfitLossResumedDashboard } from "../../../services/dashboard/dashboard.service";
import extractErrorMessage from "../../../common/utils/errorMessages";
import { colorByValue } from "../../../common/utils/color";
import { formatPriceToDolar } from "../../../common/utils/number";
import ProfitLossResumedTotals from "./profit-loss-totals";
import ProfitLossChart from "./profit-loss-chart";

const headerColumnList: IHeaderGrid[] = [
    {
        align: 'left',
        name: 'Nome da Carteira',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Total Investido',
        padding: 'normal',
        tooltip: 'Este valor NÃO INCLUI Stable Coins'
    },
    {
        align: 'left',
        name: 'Total Vendido',
        padding: 'normal',
        tooltip: 'Este valor NÃO INCLUI Stable Coins'
    },
    {
        align: 'left',
        name: 'Valor Total',
        padding: 'normal',
        tooltip: 'Este valor INCLUI Stable Coins'
    },
    {
        align: 'left',
        name: 'Ganhos/Perdas',
        padding: 'normal'
    },
    {
        align: 'right',
        name: '%',
        padding: 'normal'
    },
];

export default function ProfitLossResumedCard() {

    const [loading, setLoading] = useState(false);
    const [profitLossList, setProfitLossList] = useState<IProfitLossResumedDto[]>([]);
    const [isActive, setIsActive] = useState<boolean | null>(true);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();

    async function getProfitLossResumedDashboard() {
        try {
            var response = await tryGetProfitLossResumedDashboard(isActive);

            if (response.status === 200) {
                setProfitLossList(response.data);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    useEffect(() => {
        setLoading(true);

        getProfitLossResumedDashboard();

        setLoading(false);

    }, [isActive]);

    const handleIsActiveOnChange = (event: SelectChangeEvent) => {
        const value = event.target.value;

        if (value === 'true') {
            setIsActive(true);
        } else if (value === 'false') {
            setIsActive(false);
        } else {
            setIsActive(null);
        }
    }

    return (
        <>
            {showLoading ? <Loader /> : false}
            <Typography
                variant="h6"
                fontWeight={600}
                gutterBottom
                sx={{ color: 'text.primary', letterSpacing: 0.5 }}
            >
                Visão Sintética
            </Typography>

            <Grid container spacing={3} alignItems="stretch">
                <Grid size={{ xs: 12, lg: 7 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Grid container justifyContent="flex-start">
                        <Stack spacing={1} marginTop={2} marginBottom={2}>
                            <InputLabel sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                Status Carteira
                            </InputLabel>
                            <Select
                                value={isActive === null ? 'null' : isActive.toString()}
                                size="small"
                                onChange={(event) => handleIsActiveOnChange(event)}
                                MenuProps={{ disableScrollLock: true }}
                            >
                                <MenuItem value="null">Todas</MenuItem>
                                <MenuItem value="true">Activa</MenuItem>
                                <MenuItem value="false">Inactiva</MenuItem>
                            </Select>
                        </Stack>
                    </Grid>
                    <Card sx={{ border: '1px solid rgba(255,215,0,0.1)', flex: 1 }}>
                        <Box>
                            <GridContent>
                                <GridHeader headerProps={headerColumnList} />
                                <TableBody>
                                    {profitLossList && profitLossList.map((profitLoss, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                backgroundColor: index % 2 !== 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                                '&:hover': { backgroundColor: 'rgba(255,215,0,0.05)' },
                                                transition: 'background-color 0.15s ease',
                                            }}
                                        >
                                            <TableCell align="left">
                                                <Typography fontWeight={500}>{profitLoss.walletName}</Typography>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Typography>
                                                    {formatPriceToDolar(profitLoss.totalInvested)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Typography>
                                                    {formatPriceToDolar(profitLoss.totalSelled)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Typography>
                                                    {formatPriceToDolar(profitLoss.totalAmount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Typography color={colorByValue(profitLoss.profitLoss)}>
                                                    {formatPriceToDolar(profitLoss.profitLoss)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={`${profitLoss.profitLossPercentage}%`}
                                                    size="small"
                                                    color={colorByValue(profitLoss.profitLossPercentage) as 'success' | 'error'}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, fontSize: '0.72rem', borderRadius: '6px' }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <ProfitLossResumedTotals profitLossList={profitLossList} />
                                </TableBody>
                            </GridContent>
                        </Box>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, lg: 5 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mt: { xs: 0, lg: 7 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <ProfitLossChart profitLossList={profitLossList} />
                    </Box>
                </Grid>
            </Grid>
        </>
    )
}
